import { makeStyles } from '@mui/styles';
import Modal from 'apollo-react/components/Modal';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { setSignOffDialogVisibility } from '../actions/AuthActions';
import { onSubmitEnd, setFinishSubmit } from '../actions/WorkflowActions';
import { useTranslation } from '../custom-hooks/useTranslation';
import { translate } from '../form-designer/utils/form-manager.utils';
import { selectShowFEDSSOPopup, selectShowSignOffDialog } from '../selectors/AuthSelectors';
import {
  closeSSOPopup,
  focusSSOPopup,
  isSetSSOPopupWindow,
  setSSOPopup,
} from '../utils/ssoPopupUtil';

const useStyles = makeStyles({
  contentWrapper: {
    height: 400,
    width: 600,

    '& [class*="MuiDialogActions"]': {
      marginBottom: 20,
      marginTop: 5,
    },
  },
  content: {
    height: '100%',
    textAlign: 'center',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  contentAlert: {
    fontSize: '0.8rem',
    lineHeight: '1rem',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
  },
  contentMessage: {
    fontWeight: 500,
  },
  screenCover: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 3003,
  },
});

const IdpDialog = ({
  isSignOffStep,
  onSubmitEndAction,
  setFinishSubmitAction,
  setSignOffDialogVisibilityAction,
  showSignOffDialog,
  showFEDSSOPopup,
  signatureAlert,
  signOffMessage,
  signOffPurpose,
  url,
}) => {
  const translateString = useTranslation();
  const classes = useStyles();

  const [signOffSecondStep, setSignOffSecondStep] = useState(false);

  useEffect(() => {
    if (!isSetSSOPopupWindow() && (showSignOffDialog || showFEDSSOPopup)) {
      if (!isSignOffStep || signOffSecondStep) {
        localStorage.setItem('isSSOPopupOpen', 'true');

        const popup = window.open(
          url,
          '_blank',
          'toolbar=no,scrollbars=yes,resizable=no,top=100,left=200,width=800,height=900'
        );

        const check = setInterval(() => {
          if (popup && popup.closed) {
            localStorage.removeItem('isSSOPopupOpen');
            clearInterval(check);
            setSSOPopup(null);
            setSignOffSecondStep(false);
            !isSignOffStep && setSignOffDialogVisibilityAction(false);
          }
        }, 1000);

        setSSOPopup(popup);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSignOffDialog, showFEDSSOPopup, signOffSecondStep]);

  const openSSOPopup = () => {
    if (!isSignOffStep || signOffSecondStep) {
      focusSSOPopup();
    } else {
      setSignOffSecondStep(true);
    }
  };

  const handleCloseDialog = () => {
    onSubmitEndAction();
    setFinishSubmitAction(false);
    setSignOffDialogVisibilityAction(false);
    closeSSOPopup();
  };

  const buttonProps = [
    {
      label: translateString('Cancel'),
      onClick: handleCloseDialog,
    },
    {
      label: translateString('Sign-off'),
      onClick: openSSOPopup,
    },
  ];

  const wrapperProps = isSignOffStep
    ? {
        classes: {
          paper: classes.contentWrapper,
        },
        maxWidth: false,
      }
    : {
        fullScreen: true,
      };

  if (isSignOffStep) {
    return (
      <Modal
        title={signOffPurpose}
        disableBackdropClick
        disableEscapeKeyDown
        open={isSignOffStep}
        onClose={handleCloseDialog}
        buttonProps={buttonProps}
        {...wrapperProps}
      >
        <div className={classes.content}>
          {isSignOffStep && <div className={classes.contentMessage}>{signOffMessage}</div>}
          {isSignOffStep && (
            <div className={classes.contentAlert}>
              <div>{signatureAlert}</div>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  return showFEDSSOPopup ? (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div className={classes.screenCover} onClick={openSSOPopup} role="dialog"></div>
  ) : null;
};

const mapStateToProps = (state) => {
  const showSignOffDialog = selectShowSignOffDialog(state);
  const showFEDSSOPopup = selectShowFEDSSOPopup(state);
  const { workflow, translations } = state;

  const formKey = get(state, 'workflow.targetForm', '');
  const actionName = get(state, `formDesigner.${formKey}.workflowInfo.actionName`, '');
  const formInfo = get(state, `formDesigner.${formKey}.data.formInfo`, {});
  const auth = get(state, 'auth', {});
  const isSignOffStep = get(auth, 'isSignOffStep');
  const licId = get(auth, 'licId');
  const signOffMeaningKey = workflow?.signOffMeaning;
  const signOffMeaning = translate(translations, signOffMeaningKey ?? '');
  const signatureAlert = translate(translations, 'SignatureAlert');
  const signOffPurpose = translate(translations, 'SignOffPurpose');
  const signOffPurposeMsg = translate(translations, 'SignOffPurposeMsg');
  const stepName = get(formInfo, 'stepName');
  const translatedStepName = get(formInfo, 'translatedStepName');
  const signOffStepName = translatedStepName || actionName || stepName || '';
  const storedUrlKey = isSignOffStep ? 'taskSignOffUrl' : 'loginUrl';
  const licIdParam = isSignOffStep ? `&licId=${licId}` : '';
  const urlStored = localStorage.getItem(storedUrlKey);
  const url = `${urlStored}${licIdParam}`;
  const signOffMessage =
    signOffPurposeMsg && `${signOffPurposeMsg} ${signOffMeaning} ${signOffStepName}`;

  return {
    isSignOffStep,
    showSignOffDialog,
    showFEDSSOPopup,
    signatureAlert,
    signOffMessage,
    signOffPurpose,
    signOffMeaning,
    url,
  };
};

const mapDispatchToProps = {
  onSubmitEndAction: onSubmitEnd,
  setFinishSubmitAction: setFinishSubmit,
  setSignOffDialogVisibilityAction: setSignOffDialogVisibility,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(IdpDialog);
